import {Component, OnInit, Inject, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {IrbClientsService} from '../irb-clients.service';
import {AppService} from '@irb/app.service';
import {deletePortfolios, Portfolio} from '../irb-clients.model';
import {FormControl} from '@angular/forms';
import {PortfolioService} from '../portfolios/portfolio.service';
import {IrbAppEvents} from '@irb/app.events';
import {
  DeletePortfolioDialogGridLayoutService
} from "@irb/irb-clients/delete-portfolio-dialog/delete-portfolio-dialog.grid-layout.service";
import {IAlertDialogData} from "@irb/app.model";

@Component({ standalone: false,
  selector: 'irb-delete-portfolio-dialog',
  templateUrl: './delete-portfolio-dialog.component.html',
  styleUrls: ['./delete-portfolio-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class DeletePortfolioDialogComponent implements OnInit, OnDestroy {
  portfolioTarget = new FormControl();
  portfolios: Array<any>;
  deletePortfolios: Array<deletePortfolios> = [];
  deleteContinueFunction: Function;
  grid: any;
  gridOptions: any;
  progressBarValue = 0;
  showProgressBar = false;
  updateStatusCount = 0;
  progressTimer: any = null;
  isUiTest = false;
  deletePortfolioThreshold = 50;

  constructor(public dialogRef: MatDialogRef<DeletePortfolioDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public mdd: any,
              private cs: IrbClientsService,
              private as: AppService,
              private readonly portfolioService: PortfolioService, private gls: DeletePortfolioDialogGridLayoutService) {
    this.getUserDetails();
  }

  getUserDetails(): void {
    this.as.getUserDetails().subscribe((res: any) => {
      if (res) {
        this.deletePortfolioThreshold = res.deletePortfolioThreshold;
      }
    });
  }

  ngOnInit(): void {
    this.gridOptions = {
      columnDefs: this.gls.getColumnDefs(),
      floatingFilter: false,
      overlayNoRowsTemplate: "<span class='ag-overlay-loading-center ag-overlay-message'>No results.</span>",
      context: {componentParent: this}
    };
    this.portfolios = this.mdd.portfolios;
    this.deleteContinueFunction = this.mdd.deleteContinueFunction || null;
  }

  close() {
    this.showProgressBar = false;
    this.dialogRef.close();
  }

  deleteSelectedPortfolios() {
    this.as.fireEvent(IrbAppEvents.SHOW_WAIT);
    this.mdd.portfolios.forEach((item: any) => {
      this.deletePortfolios.push({portfolioId: item.portfolioId, versionId: item.versionId})
    });

    if (this.deletePortfolios.length > this.deletePortfolioThreshold) {
      this.deleteSelectedPortfoliosAsync();
      return;
    }
    this.portfolioService.deleteSelectedPortfolios(this.deletePortfolios).subscribe((data: any) => {
      this.proceedOnComplete(data);
    });
  }

  deleteSelectedPortfoliosAsync() {
    this.portfolioService.deleteSelectedPortfoliosAsync(this.deletePortfolios).subscribe((data: any) => {
      this.updateStatusCount = 0;
      this.progressTimer && clearTimeout(this.progressTimer);
      this.showProgressBar = true;
      this.actionStatusCheckIntervalStart(data.responseUtilityMap.statusId);
      this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
    }, () => {
      this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
    });
  }

  proceedOnComplete(data: any) {
    this.deleteContinueFunction(data);
    this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
    this.close();
  }

  updateProgressBarStatus(response: any) {
    const cnt: any = response.failedRecCount + response.successRecCount;
    const rcnt: any = response.recievedCount;
    if (rcnt > 0) {
      const progressPercentage = Math.floor((cnt * 100) / rcnt);
      this.progressBarValue = progressPercentage;
    }
    let timeout = (this.updateStatusCount > 10) ? 8000 : (this.updateStatusCount > 2) ? 3000 : 1500;
    if (this.isUiTest)
      timeout = 0;
    this.updateStatusCount++;
    this.progressTimer = setTimeout(() => {
      this.actionStatusCheckIntervalStart(response.statusId);
    }, timeout);
  }

  actionStatusCheckIntervalStart(statusId: any) {
    this.portfolioService.actionStatusCheck(statusId).subscribe((statusResponse: any) => {
      if (statusResponse.responseStatus === "SUCCESS") {
        const response = statusResponse.responseData;
        const reqStatus = response.reqStatus.trim().toUpperCase();
        if (reqStatus === 'FAILED') {
          if (response.failMsg && response.failMsg.toUpperCase() === 'STALEDATAEXCEPTION')
            this.showStaleDataDialog();
          else
            this.actionStatusCheckFailed();
        }

        if (reqStatus === 'STARTED' || reqStatus === 'NEW' || reqStatus === 'IN_PROGRESS') {
          this.updateProgressBarStatus(response);
        }
        if (reqStatus === 'COMPLETED') {
          this.close();
          this.progressTimer && clearTimeout(this.progressTimer);
          this.proceedOnComplete({responseData: response.recievedCount})
        }
      } else {
        this.close();
        this.progressTimer && clearTimeout(this.progressTimer);
      }
    }, () => {
      this.close();
      this.progressTimer && clearTimeout(this.progressTimer);
    });
  }

  actionStatusCheckFailed() {
    this.close();
    this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
      dType: 'ERROR',
      dTitle: '',
      dProceedBtnLabel: '',
      msg: 'An error occurred processing your request.',
      onProceed: () => {
      }
    } as IAlertDialogData);
  }

  showStaleDataDialog() {
    this.close();
    this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
      dTitle: 'Refresh Required',
      dProceedBtnLabel: 'Refresh',
      msg: "You are attempting to delete portfolios which were modified by another user. Please refresh page to see latest changes.",
      onProceed: () => {
        this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
      }
    });
  }

  onGridReady(params: any) {
    this.grid = params;
  }

  ngOnDestroy() {
    this.progressTimer && clearTimeout(this.progressTimer);
  }
}
